'use client';

import { useCallback, useState } from 'react';
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from '@react-google-maps/api';

import SurroundingsMapInfoWindowContent from './InfoWindowContet';

import { VillageSurrounding } from '@/services/types';
import { ISurroundingsMapDictionary } from '@/dictionaries/components/surroundingsMap/types';
import { useLocale } from '@/hooks/useLocale';

type Props = {
  dict: ISurroundingsMapDictionary;
  villageLocation: {
    lat: number;
    lng: number;
  };
  surroundings: VillageSurrounding[];
};

export default function SurroundingsMapContent({
  dict,
  villageLocation,
  surroundings,
}: Props) {
  const [map, setMap] = useState<google.maps.Map | null>(null); // unused?
  const [infoWindowPositon, setInfoWindowPosition] =
    useState<google.maps.LatLngLiteral>();
  const [infoWindowData, setInfoWindowData] = useState<VillageSurrounding>();
  const lang = useLocale();

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY || '',
  });

  const onLoad = useCallback(function callback(map: google.maps.Map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback() {
    setMap(null);
  }, []);

  function handleSurroundingClick(surrounding: VillageSurrounding) {
    const { surrounding: surroundingObj } = surrounding;
    const positon = surroundingObj.location;

    setInfoWindowPosition({ lat: positon.latitude, lng: positon.longitude });
    const translatedSurrounding =
      surroundingObj.translate.find((translate) => translate.locale === lang) ||
      surroundingObj.translate[0];

    setInfoWindowData({
      ...surrounding,
      surrounding: { ...surroundingObj, translate: [translatedSurrounding] },
    });
  }

  return (
    <div className="relative h-[60vh] min-h-[600px] overflow-hidden">
      <div className="w-full h-full absolute top-[0px] left-[0px] bg-[rgb(229, 227, 223)]">
        {isLoaded && (
          <GoogleMap
            mapContainerClassName="relative overflow-hidden w-full h-[calc(100vh-137px)]"
            center={villageLocation}
            zoom={11}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
              mapId: '3cf9f101ac9b503c',
              fullscreenControl: false,
              zoomControlOptions: { position: 3.0 },
              tilt: 7.8,
              minZoom: 7.8,
              isFractionalZoomEnabled: false,
              keyboardShortcuts: false,
              streetViewControl: false,
              mapTypeControl: false,
            }}>
            {infoWindowData && (
              <InfoWindow
                position={infoWindowPositon}
                onCloseClick={() => {
                  setInfoWindowData(undefined);
                }}>
                <SurroundingsMapInfoWindowContent
                  dict={dict}
                  surrounding={infoWindowData}
                />
              </InfoWindow>
            )}
            <Marker
              icon={{
                url: 'https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/maps/markers/pin-villa.svg',
                scaledSize: new window.google.maps.Size(55, 55),
              }}
              zIndex={100}
              position={villageLocation}
            />
            {surroundings.map((surrounding, index) => {
              const { surrounding: surroundingObj } = surrounding;
              const position = surroundingObj.location;
              const category = surroundingObj.placeCategory.code;
              return (
                <Marker
                  icon={{
                    url: `https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/maps/markers/${category}.png`,
                    scaledSize: new window.google.maps.Size(41, 41),
                  }}
                  key={index}
                  position={{
                    lat: position.latitude,
                    lng: position.longitude,
                  }}
                  onClick={() => handleSurroundingClick(surrounding)}
                />
              );
            })}
          </GoogleMap>
        )}
      </div>
    </div>
  );
}
